/**
 * Mock posts service.
 * Mirrors the real posts.service.js API with in-memory data.
 */
import { MOCK_POSTS } from '../../data/mock/mock-posts.data';
import { mockDelay, mockLog, mockId } from './mock-delay';
import { extractHashtags, extractMentions } from '../../utils/text-parser.util';

// In-memory state that resets on page refresh
let posts = [...MOCK_POSTS];
let likedPosts = new Set(['mock_post_t02', 'mock_post_w02', 'mock_post_m04', 'mock_post_p03']);

const paginate = (items, lastDoc, pageLimit) => {
  let startIndex = 0;
  if (lastDoc) {
    const idx = items.findIndex((p) => p.id === lastDoc);
    if (idx !== -1) startIndex = idx + 1;
  }
  const page = items.slice(startIndex, startIndex + pageLimit);
  const newLastDoc = page.length > 0 ? page[page.length - 1].id : null;
  const hasMore = startIndex + pageLimit < items.length;
  return { page, lastDoc: newLastDoc, hasMore };
};

export const createPost = async (params) => {
  mockLog('PostsService', 'createPost', params.type);
  await mockDelay();
  const trimmedText = (params.text || '').trim();
  const newPost = {
    id: `mock_post_${mockId()}`,
    type: params.type || 'text',
    text: trimmedText,
    imageUrl: null,
    authorId: params.author.uid,
    authorName: params.author.displayName,
    authorUsername: params.author.username,
    authorAvatarUrl: params.author.avatarUrl || null,
    likesCount: 0,
    commentsCount: 0,
    edited: false,
    tags: extractHashtags(trimmedText),
    mentions: extractMentions(trimmedText),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    workout: params.workout || null,
    meal: params.meal || null,
    progress: params.progress || null,
    _docSnapshot: null,
  };
  posts = [newPost, ...posts];
  return newPost;
};

export const getPosts = async (lastDoc = null, pageLimit = 10) => {
  mockLog('PostsService', 'getPosts', { lastDoc, pageLimit });
  await mockDelay();
  const sorted = [...posts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const { page, lastDoc: newLast, hasMore } = paginate(sorted, lastDoc, pageLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};

export const getPostsByType = async (type, lastDoc = null, pageLimit = 10) => {
  mockLog('PostsService', 'getPostsByType', { type, lastDoc, pageLimit });
  await mockDelay();
  const filtered = posts.filter((p) => p.type === type).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const { page, lastDoc: newLast, hasMore } = paginate(filtered, lastDoc, pageLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};

export const getPostById = async (postId) => {
  mockLog('PostsService', 'getPostById', postId);
  await mockDelay();
  return posts.find((p) => p.id === postId) || null;
};

export const updatePost = async (postId, { text }) => {
  mockLog('PostsService', 'updatePost', postId);
  await mockDelay();
  const idx = posts.findIndex((p) => p.id === postId);
  if (idx !== -1) {
    posts[idx] = { ...posts[idx], text: (text || posts[idx].text).trim(), edited: true, updatedAt: new Date().toISOString() };
    return posts[idx];
  }
  return null;
};

export const deletePost = async (postId) => {
  mockLog('PostsService', 'deletePost', postId);
  await mockDelay();
  posts = posts.filter((p) => p.id !== postId);
  return postId;
};

export const likePost = async (postId, userId) => {
  mockLog('PostsService', 'likePost', { postId, userId });
  await mockDelay(100, 300);
  likedPosts.add(postId);
  const idx = posts.findIndex((p) => p.id === postId);
  if (idx !== -1) posts[idx] = { ...posts[idx], likesCount: posts[idx].likesCount + 1 };
};

export const unlikePost = async (postId, userId) => {
  mockLog('PostsService', 'unlikePost', { postId, userId });
  await mockDelay(100, 300);
  likedPosts.delete(postId);
  const idx = posts.findIndex((p) => p.id === postId);
  if (idx !== -1) posts[idx] = { ...posts[idx], likesCount: Math.max(0, posts[idx].likesCount - 1) };
};

export const checkIfLiked = async (postId, userId) => {
  mockLog('PostsService', 'checkIfLiked', { postId });
  await mockDelay(50, 150);
  return likedPosts.has(postId);
};

export const getPostsByUser = async (userId, lastDoc = null, pageLimit = 10) => {
  mockLog('PostsService', 'getPostsByUser', { userId, lastDoc, pageLimit });
  await mockDelay();
  const filtered = posts.filter((p) => p.authorId === userId).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const { page, lastDoc: newLast, hasMore } = paginate(filtered, lastDoc, pageLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};

export const getPostsFeed = async (authorIds, lastDoc = null, pageLimit = 10) => {
  mockLog('PostsService', 'getPostsFeed', { authorIds: authorIds?.length, lastDoc, pageLimit });
  await mockDelay();
  const filtered = posts.filter((p) => authorIds.includes(p.authorId)).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const { page, lastDoc: newLast, hasMore } = paginate(filtered, lastDoc, pageLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};

export const getPostsFeedByType = async (authorIds, type, lastDoc = null, pageLimit = 10) => {
  mockLog('PostsService', 'getPostsFeedByType', { type, authorIds: authorIds?.length });
  await mockDelay();
  const filtered = posts.filter((p) => authorIds.includes(p.authorId) && p.type === type).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const { page, lastDoc: newLast, hasMore } = paginate(filtered, lastDoc, pageLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};

export const getLikedPosts = async (userId, lastDocCursor = null, pageLimit = 10) => {
  mockLog('PostsService', 'getLikedPosts', { userId });
  await mockDelay();
  const liked = posts.filter((p) => likedPosts.has(p.id)).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const { page, lastDoc: newLast, hasMore } = paginate(liked, lastDocCursor, pageLimit);
  return { posts: page, lastDoc: newLast, hasMore };
};
